import { useCallback, useState } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { IEmployeeDocument } from '@/@types/company';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export function useEmployeeDocumentUpload() {
  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const [isUploading, setIsUploading] = useState(false);

  const uploadDocument = useCallback((): Promise<IEmployeeDocument | null> => {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.jpg,.jpeg,.png,.pdf';

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
          toast.error('Only JPG, PNG, and PDF files are accepted');
          resolve(null);
          return;
        }

        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`File must be under ${MAX_SIZE_MB} MB`);
          resolve(null);
          return;
        }

        setIsUploading(true);
        try {
          const uploadUrl = await createUploadUrl({});
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });

          if (!response.ok) {
            toast.error('Upload failed — please try again');
            resolve(null);
            return;
          }

          const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
          resolve({ storageId, fileName: file.name });
        } catch {
          toast.error('Upload failed — please try again');
          resolve(null);
        } finally {
          setIsUploading(false);
        }
      };

      // User cancelled the file picker
      input.addEventListener('cancel', () => resolve(null));
      input.click();
    });
  }, [createUploadUrl]);

  return { uploadDocument, isUploading };
}
